import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, API_BASE, formatEUR } from "@/lib/api";
import { UploadSimple, CheckCircle, WarningCircle, Copy, FileText, Download, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const SAMPLE_CSV = `nom,client,statut,etoiles,type_contact,apporteur,montant,honoraires,commission_banque,assurance,taux_remuneration_pct,retro_apporteur,retro_parrain,banques,etape
Dupont — Villa neuve,Jean Dupont,en_cours,3,rdv_fait,Emma Bardin,420000,4500,4200,500,70,700,0,BNP/LCL,accord_banque
Martin — Appart invest,Lucas Martin,en_cours,2,a_appeler,,175000,2500,1750,300,75,0,0,Crédit Agricole,compromis
`;

export default function ImportCSV() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imported, setImported] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const run = async (dry = true) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/import-dossiers?dry_run=${dry}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      if (!dry) {
        setImported(true);
        toast.success(`${res.data.summary.created} dossier(s) importé(s)`);
      } else {
        toast.info(`Preview : ${res.data.summary.valid} valides · ${res.data.summary.invalid} erreurs · ${res.data.summary.duplicates} doublons`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur import");
    } finally { setLoading(false); }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "credit-flow-sample.csv";
    a.click(); URL.revokeObjectURL(url);
  };

  const sum = preview?.summary;

  return (
    <Layout title="Import CSV" centerTitle>
      <button onClick={() => navigate("/")} className="hidden md:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 font-semibold">← Retour</button>
      <div className="hidden md:block mb-5">
        <div className="overline">Paramètres</div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900">Import CSV</h1>
        <p className="text-slate-500 mt-1">Preview obligatoire avant import réel.</p>
      </div>

      {/* Sample download */}
      <div className="card-tinted-blue p-4 mb-4 flex items-center justify-between gap-3" data-testid="sample-bar">
        <div className="flex items-center gap-2.5">
          <FileText size={22} color="var(--cf-primary)" weight="fill" />
          <div>
            <div className="font-display font-extrabold text-slate-900 text-sm">Modèle CSV</div>
            <div className="text-[12px] text-slate-500">Télécharge un exemple pour référencer les colonnes.</div>
          </div>
        </div>
        <button onClick={downloadSample} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white text-[color:var(--cf-primary)] text-xs font-bold border border-[color:var(--cf-primary-border)]" data-testid="download-sample-btn">
          <Download size={14} weight="bold" /> Modèle
        </button>
      </div>

      {/* Upload zone */}
      <div className="card-base p-5 mb-4" data-testid="upload-card">
        <div className="overline mb-2">Fichier CSV</div>
        <input
          ref={inputRef}
          type="file" accept=".csv,text/csv"
          onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); setImported(false); }}
          className="hidden" data-testid="file-input"
        />
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-slate-300 hover:border-[color:var(--cf-primary)] rounded-2xl p-8 text-center transition-colors"
          data-testid="upload-btn"
        >
          <UploadSimple size={32} className="mx-auto text-slate-400 mb-2" />
          <div className="font-display font-extrabold text-slate-900">{file ? file.name : "Choisir un fichier"}</div>
          <div className="text-[12px] text-slate-500 mt-1">
            {file ? `${(file.size / 1024).toFixed(1)} Ko` : "CSV, colonnes: nom, client, statut, etoiles, type_contact, etc."}
          </div>
        </button>

        {file && !imported && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={() => run(true)} disabled={loading}
              className="py-3 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200 disabled:opacity-40"
              data-testid="preview-btn"
            >
              {loading ? "…" : "Prévisualiser"}
            </button>
            <button
              onClick={() => run(false)} disabled={loading || !preview || !sum?.valid}
              className="py-3 rounded-xl bg-[color:var(--cf-primary)] text-white text-sm font-bold hover:bg-[color:var(--cf-primary-hover)] disabled:opacity-40 shadow-[0_4px_12px_rgba(45,110,255,0.3)]"
              data-testid="import-btn"
              title={!preview ? "Prévisualise d'abord" : !sum?.valid ? "Aucune ligne valide" : ""}
            >
              Importer {sum?.valid ? `(${sum.valid})` : ""}
            </button>
          </div>
        )}
      </div>

      {/* Result / preview */}
      {preview && (
        <div data-testid="preview-result">
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="card-base p-3 text-center">
              <div className="text-[10px] text-slate-500 font-bold uppercase">Total</div>
              <div className="metric-value text-xl text-slate-900">{sum.total_rows}</div>
            </div>
            <div className="card-base p-3 text-center">
              <div className="text-[10px] text-[color:var(--cf-success)] font-bold uppercase">Valides</div>
              <div className="metric-value text-xl text-[color:var(--cf-success)]">{sum.valid}</div>
            </div>
            <div className="card-base p-3 text-center">
              <div className="text-[10px] text-[color:var(--cf-danger)] font-bold uppercase">Erreurs</div>
              <div className="metric-value text-xl text-[color:var(--cf-danger)]">{sum.invalid}</div>
            </div>
            <div className="card-base p-3 text-center">
              <div className="text-[10px] text-[color:var(--cf-warning)] font-bold uppercase">Doublons</div>
              <div className="metric-value text-xl text-[color:var(--cf-warning)]">{sum.duplicates}</div>
            </div>
          </div>

          {imported && (
            <div className="card-tinted-green p-4 mb-4 flex items-center gap-2" data-testid="import-success">
              <CheckCircle size={24} weight="fill" color="var(--cf-success)" />
              <div className="flex-1">
                <div className="font-display font-extrabold text-slate-900">{sum.created} dossier(s) importé(s)</div>
                <div className="text-[12px] text-slate-500">Ils apparaissent maintenant dans ton pipeline.</div>
              </div>
              <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[color:var(--cf-success)] text-white text-xs font-bold" data-testid="goto-commando-btn">
                Commando <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          )}

          {/* Headers mapping */}
          <div className="card-base p-4 mb-4" data-testid="headers-mapping">
            <div className="overline mb-2">Colonnes reconnues</div>
            <div className="flex flex-wrap gap-1.5">
              {preview.headers_mapped.map((h) => (
                <span key={h} className="pill pill-green text-[11px]">{h}</span>
              ))}
              {preview.headers_original
                .filter((h, i) => {
                  // mark non-mapped as gray
                  return preview.headers_mapped.length < preview.headers_original.length;
                })
                .slice(0, 0)}
            </div>
            {preview.headers_original.length > preview.headers_mapped.length && (
              <div className="text-[11px] text-slate-400 mt-2">
                Colonnes ignorées : {preview.headers_original.length - preview.headers_mapped.length}
              </div>
            )}
          </div>

          {/* Errors */}
          {preview.errors?.length > 0 && (
            <div className="card-base p-4 mb-4" data-testid="errors-list">
              <div className="flex items-center gap-2 mb-3">
                <WarningCircle size={18} color="var(--cf-danger)" weight="fill" />
                <div className="font-display font-extrabold text-slate-900">Lignes invalides ({preview.errors.length})</div>
              </div>
              <ul className="space-y-2">
                {preview.errors.map((e, i) => (
                  <li key={i} className="bg-[color:var(--cf-danger-soft)] border border-[color:var(--cf-danger-border)] rounded-lg p-3">
                    <div className="text-[12px] text-slate-500 font-bold">Ligne {e.line} · {e.nom || "—"}</div>
                    <ul className="mt-1 text-[13px] text-[color:var(--cf-danger-text)] font-medium">
                      {e.messages.map((m, j) => <li key={j}>• {m}</li>)}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Duplicates */}
          {preview.duplicates?.length > 0 && (
            <div className="card-base p-4 mb-4" data-testid="duplicates-list">
              <div className="flex items-center gap-2 mb-3">
                <Copy size={18} color="var(--cf-warning)" weight="fill" />
                <div className="font-display font-extrabold text-slate-900">Doublons ignorés ({preview.duplicates.length})</div>
              </div>
              <ul className="space-y-2">
                {preview.duplicates.map((d, i) => (
                  <li key={i} className="bg-[color:var(--cf-warning-soft)] border border-[color:var(--cf-warning-border)] rounded-lg p-3 text-[13px]">
                    <div className="font-bold text-slate-900">{d.nom}</div>
                    <div className="text-slate-500 text-[12px]">Ligne {d.line} · {d.client || "—"} · {d.reason}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview valid */}
          {preview.preview?.length > 0 && (
            <div className="card-base p-4 mb-4" data-testid="valid-list">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={18} color="var(--cf-success)" weight="fill" />
                <div className="font-display font-extrabold text-slate-900">Aperçu des dossiers valides ({preview.preview.length}{sum.valid > preview.preview.length ? ` / ${sum.valid}` : ""})</div>
              </div>
              <ul className="space-y-2">
                {preview.preview.map((p, i) => (
                  <li key={i} className="bg-[color:var(--cf-success-soft)] border border-[color:var(--cf-success-border)] rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{p.nom}</div>
                        <div className="text-[12px] text-slate-500">{p.client_name || "—"} · {p.status} · {p.etoiles}★</div>
                        {p.banques?.length > 0 && (
                          <div className="text-[11px] text-slate-400 mt-0.5">{p.banques.join(" / ")}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="metric-value text-base text-[color:var(--cf-success)]">{formatEUR(p.ca_net)}</div>
                        <div className="text-[10px] text-slate-400">
                          net (taux {p.taux_remuneration_pct}%{p.retro_apporteur > 0 ? ` − ${formatEUR(p.retro_apporteur)}` : ""}{p.retro_parrain > 0 ? ` − ${formatEUR(p.retro_parrain)}` : ""})
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-slate-400 mt-6 text-center mb-8">
        Colonnes supportées (alias reconnus) : nom, client/client_name, statut/status (en_cours/signe/perdu),
        etoiles (1-3), type_contact (a_appeler / rdv_a_faire / rdv_fait), apporteur, montant/montant_bien,
        honoraires, commission_banque, assurance, taux_remuneration_pct (70-90),
        retro_apporteur (€), retro_parrain (€), banques (séparateurs / ; | ,), etape.
      </div>
    </Layout>
  );
}
